import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { StatusYearListControl } from '@/module/Author/Project/components/form/StatusYearListControl';
import { CompetitionSelectControl } from '@/module/Nomen/Competition/CompetitionSelectControl';
import { CompetitionTypeSelectControl } from '@/module/Nomen/CompetitionType/CompetitionTypeSelectControl';
import { FinancingTypeSelectControl } from '@/module/Nomen/FinancingType/FinancingTypeSelectControl';
import { ProjectContributionSelectControl } from '@/module/Nomen/ProjectContribution/ProjectContributionSelectControl';
import { ProjectMembershipSelectControl } from '@/module/Nomen/ProjectMembership/ProjectMembershipSelectControl';

import {
  AuthorListControl,
  Content,
  Form,
  FormSubmit,
  NumberInputControl,
  PublishedAtControl,
  TextInputControl,
} from '@/core/components';

import { CurrencySelectControl } from '@feature/utils';

import { ProjectSchema } from './model/ProjectSchema';

interface ProjectFormProps extends FormSubmit<ProjectSchema> {
  title: string;
}

export function ProjectForm(props: ProjectFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const { control, handleSubmit, watch } = useForm<ProjectSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  const competition = watch('competition');
  const showCompetitionName = competition?.name === 'Altele';

  return (
    <Form
      title={title}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack>
        <Content type="full">
          <TextInputControl
            name="title"
            control={control}
            label={t('projects.form.fields.title')}
            isSubmitting={isSubmitting}
            withAsterisk
          />
          <Group align="flex-end">
            <NumberInputControl
              name="contractValue"
              control={control}
              label={t('projects.form.fields.contractValue')}
              description={t('projects.form.fields.contractValueDescription')}
              isSubmitting={isSubmitting}
              allowDecimal
              withAsterisk
              min={0}
            />
            <CurrencySelectControl
              name="currency"
              control={control}
              label={t('projects.form.fields.currency')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <ProjectContributionSelectControl
              name="contribution"
              control={control}
              label={t('projects.form.fields.contribution')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
          </Group>
          <NumberInputControl
            name="percentObtainedOfMinScore"
            control={control}
            label={t('projects.form.fields.percentObtainedOfMinScore')}
            description={t('projects.form.fields.percentObtainedOfMinScore')}
            isSubmitting={isSubmitting}
            allowDecimal={true}
            min={0}
            max={100}
          />
          <Group>
            <CompetitionSelectControl
              name="competition"
              control={control}
              label={t('projects.form.fields.competition')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <CompetitionTypeSelectControl
              name="competitionType"
              control={control}
              label={t('projects.form.fields.competitionType')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
          </Group>
          {showCompetitionName && (
            <TextInputControl
              name="competitionName"
              control={control}
              label={t('projects.form.fields.competitionName')}
              isSubmitting={isSubmitting}
            />
          )}
          <Group>
            <FinancingTypeSelectControl
              name="financing.type"
              control={control}
              label={t('projects.form.fields.financingType')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <TextInputControl
              name="financing.financerName"
              control={control}
              label={t('projects.form.fields.financerName')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
          </Group>
          <Group>
            <TextInputControl
              name="registrationNumber"
              control={control}
              label={t('projects.form.fields.registrationNumber')}
              isSubmitting={isSubmitting}
              withAsterisk
            />
            <TextInputControl
              name="contractNo"
              control={control}
              label={t('projects.form.fields.contractNo')}
              isSubmitting={isSubmitting}
            />
          </Group>
        </Content>
        <Content type="full">
          <StatusYearListControl
            name="statusYear"
            control={control}
            label={t('projects.form.fields.statusYear')}
            isSubmitting={isSubmitting}
          />
        </Content>
        <Content type="full">
          <Group>
            <PublishedAtControl
              name="startDate"
              control={control}
              label={t('projects.form.fields.startDate')}
              isSubmitting={isSubmitting}
            />
            <PublishedAtControl
              name="endDate"
              control={control}
              label={t('projects.form.fields.endDate')}
              isSubmitting={isSubmitting}
            />
          </Group>
        </Content>
        <Content type="full">
          <AuthorListControl
            name="authors"
            control={control}
            isSubmitting={isSubmitting}
            label={t('projects.form.fields.authorsTitle')}
            renderMembership={({ membershipName }) => (
              <ProjectMembershipSelectControl
                label={t('authors.form.fields.membership')}
                // @ts-expect-error incomplete type
                name={membershipName}
                control={control}
                isSubmitting={isSubmitting}
                isDisabled={isSubmitting}
              />
            )}
          />
        </Content>
      </Stack>
    </Form>
  );
}
